import { useCallback, useEffect, useReducer, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
  approveAssistantProposal,
  rejectAssistantProposal,
  streamAssistantChat,
} from "./assistantApi";
import { assistantReducer, initialAssistantState } from "./assistantProtocol";

/** Conversation state for one assistant panel: send, stop, approve or reject a proposal. */
const useAssistantChat = (surface) => {
  const [state, dispatch] = useReducer(assistantReducer, initialAssistantState);
  const abortRef = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => () => abortRef.current?.abort(), []);

  const send = useCallback(
    async (text) => {
      const message = text.trim();
      if (!message || state.status === "streaming") return;
      dispatch({ type: "send", text: message });
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        await streamAssistantChat({
          body: {
            conversationId: state.conversationId,
            surface,
            message,
            // The page the user is on, so "this client" can be resolved by the agent.
            context: { path: pathname },
          },
          signal: controller.signal,
          onEvent: (event) => dispatch({ type: "event", event }),
        });
        dispatch({ type: "finish" });
      } catch (err) {
        dispatch({
          type: "finish",
          error:
            err?.name === "AbortError"
              ? "Stopped."
              : err?.message || "Could not reach the assistant.",
        });
      } finally {
        abortRef.current = null;
      }
    },
    [state.status, state.conversationId, surface, pathname]
  );

  const stop = useCallback(() => abortRef.current?.abort(), []);

  const decide = useCallback(async (proposalId, decision) => {
    dispatch({ type: "proposal", id: proposalId, status: "working" });
    try {
      const result =
        decision === "approve"
          ? await approveAssistantProposal(proposalId)
          : await rejectAssistantProposal(proposalId);
      dispatch({
        type: "proposal",
        id: proposalId,
        status: decision === "approve" ? "approved" : "rejected",
        note: result?.message,
      });
    } catch (err) {
      dispatch({ type: "proposal", id: proposalId, status: "pending", note: err.message });
    }
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    dispatch({ type: "reset" });
  }, []);

  return { ...state, send, stop, decide, reset };
};

export default useAssistantChat;
